import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link2, FileUp, ClipboardType, Loader2, Sparkles, X, FileText } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

export const JobIntakeWidget = ({ onSuccess }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const fileRef = useRef(null);
    const [tab, setTab] = useState("url");
    const [url, setUrl] = useState("");
    const [text, setText] = useState("");
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);

    const onFile = (f) => {
        if (!f) return;
        const name = f.name.toLowerCase();
        if (!name.endsWith(".pdf") && !name.endsWith(".docx") && !name.endsWith(".doc")) {
            toast.error("Please upload a PDF or Word (.docx) file.");
            return;
        }
        if (f.size > 8 * 1024 * 1024) {
            toast.error("File too large. Max 8 MB.");
            return;
        }
        setFile(f);
    };

    const submit = async () => {
        const fd = new FormData();
        fd.append("source_type", tab);
        if (tab === "url") {
            if (!url.trim()) { toast.error("Paste the job posting URL."); return; }
            fd.append("url", url.trim());
        } else if (tab === "file") {
            if (!file) { toast.error("Attach your job description (PDF or Word)."); return; }
            fd.append("file", file);
        } else {
            if (text.trim().length < 100) { toast.error("Paste the full job description (at least 100 characters)."); return; }
            fd.append("text", text.trim());
        }
        try {
            setLoading(true);
            const { data } = await api.post("/jobs/intake", fd, { headers: { "Content-Type": "multipart/form-data" } });
            if (typeof onSuccess === "function") {
                // Caller (e.g. /employers/signup) decides the next step —
                // usually a /thank-you-employer redirect so conversion pixels fire.
                onSuccess(data);
                return;
            }
            if (data.authenticated) {
                toast.success("Role parsed — review the details before publishing.");
                navigate(`/employer/jobs/review/${data.intake_id}`);
            } else {
                localStorage.setItem("simonara_pending_intake", JSON.stringify({ intake_id: data.intake_id, claim_token: data.claim_token }));
                toast.success("Job received. Create your employer profile to start the AI analysis.");
                navigate("/signup?role=employer");
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card-soft p-8" data-testid="job-intake-widget">
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="rounded-full h-auto p-1.5 bg-muted w-full grid grid-cols-3">
                    <TabsTrigger value="url" data-testid="intake-tab-url" className="rounded-full py-2.5 text-sm font-semibold data-[state=active]:bg-card data-[state=active]:shadow-sm">
                        <Link2 size={14} className="mr-1.5" /> Paste URL
                    </TabsTrigger>
                    <TabsTrigger value="file" data-testid="intake-tab-file" className="rounded-full py-2.5 text-sm font-semibold data-[state=active]:bg-card data-[state=active]:shadow-sm">
                        <FileUp size={14} className="mr-1.5" /> Upload
                    </TabsTrigger>
                    <TabsTrigger value="text" data-testid="intake-tab-text" className="rounded-full py-2.5 text-sm font-semibold data-[state=active]:bg-card data-[state=active]:shadow-sm">
                        <ClipboardType size={14} className="mr-1.5" /> Paste text
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="url" className="mt-6">
                    <Input
                        value={url} onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://linkedin.com/jobs/... or any job posting URL"
                        data-testid="intake-url-input"
                    />
                    <p className="mt-2 text-xs text-muted-foreground">
                        Works on most company career pages. If a site blocks reading (LinkedIn/Indeed sometimes do), we'll ask you to paste the description instead.
                    </p>
                </TabsContent>

                <TabsContent value="file" className="mt-6">
                    <div
                        onClick={() => fileRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); }}
                        onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer?.files?.[0]); }}
                        data-testid="intake-file-drop"
                        className={`cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center transition-colors ${file ? "border-emerald-500 bg-accent" : "border-border bg-muted/50 hover:border-emerald-500/50"}`}
                    >
                        {file ? (
                            <div className="flex items-center justify-center gap-3">
                                <FileText size={20} className="text-emerald-500" />
                                <div className="text-left">
                                    <div className="font-semibold text-foreground text-sm">{file.name}</div>
                                    <div className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(0)} KB · Click to replace</div>
                                </div>
                                <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="ml-2 p-1 rounded-full hover:bg-card" aria-label="Remove file">
                                    <X size={15} />
                                </button>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center gap-2 text-muted-foreground">
                                <FileUp size={24} className="text-foreground" />
                                <div className="text-sm font-medium text-foreground">Drop your job description here, or click to browse</div>
                                <div className="text-xs">PDF or Word · Max 8 MB</div>
                            </div>
                        )}
                        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" onChange={(e) => onFile(e.target.files?.[0])} data-testid="intake-file-input" className="hidden" />
                    </div>
                </TabsContent>

                <TabsContent value="text" className="mt-6">
                    <Textarea
                        value={text} onChange={(e) => setText(e.target.value)} rows={7}
                        placeholder="Paste the full job description here…"
                        data-testid="intake-text-input"
                    />
                </TabsContent>
            </Tabs>

            <button onClick={submit} disabled={loading} data-testid="intake-submit" className="btn-green w-full mt-6 disabled:opacity-60">
                {loading ? <><Loader2 size={18} className="mr-2 animate-spin" /> Reading your job…</> : <><Sparkles size={17} className="mr-2" /> Find Talent</>}
            </button>
            {!user && (
                <p className="mt-3 text-center text-xs text-muted-foreground">
                    You'll create your employer profile in the next step — then AI analysis starts instantly.
                </p>
            )}
        </div>
    );
};

export default JobIntakeWidget;
